/**
 * Surface 3 — Public tracking view (tracking/spec.md). A resident opens their link (or types their
 * code) to see ONE thing: what's happening with their concern. HARD privacy boundary (NFR-3 / FR-12):
 * this renders the server's PUBLIC PROJECTION only (mockDb.getPublicTracking) — no reporter PII, no
 * assignee, no internal notes/attachments, no coordinates. The document structurally cannot contain
 * them. Replaces the earlier stub.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { RequestStatus } from "@td/shared";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { useTheme } from "../theme/ThemeProvider.js";
import type { InspectionTypeDef } from "../api/types.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { StatusPill } from "../components/StatusPill.js";
import { KORAStatusPill } from "../components/KORAStatusPill.js";
import { PermitStatusPill } from "../components/PermitStatusPill.js";
import { StatusTimeline, type TimelineNode } from "../components/StatusTimeline.js";
import { AttachmentTile } from "../components/AttachmentTile.js";
import { Lightbox } from "../components/Lightbox.js";
import { Button } from "../components/Button.js";
import { Field } from "../components/Field.js";
import { CaptchaField } from "../components/CaptchaField.js";
import { useCaptcha } from "../components/useCaptcha.js";
import { Skeleton, SkeletonRegion } from "../components/Skeleton.js";
import { CopyIcon, CheckIcon, SpinnerIcon } from "../components/icons.js";
import { STATUS_RESIDENT_LINE } from "../components/status.js";
import { KORA_STATUS_LABELS, type KORAStatus } from "../api/types.js";
import { PERMIT_STATUS_LABELS, type PermitStatus } from "../api/types.js";
import { absoluteDate, relativeTime, shortDate } from "../components/format.js";
import {
  getPublicTracking,
  addResidentComment,
  submitSatisfactionSurvey,
  ApiError,
  type PublicTracking,
} from "../api/index.js";
import { CAPTCHA_ENABLED } from "../api/wafCaptcha.js";
import type { AttachmentView } from "../api/types.js";

const MAX_COMMENT_LEN = 2000;
const MAX_SURVEY_COMMENT_LEN = 2000;

type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "found"; data: PublicTracking; fetchedAt: number }
  | { kind: "notfound" }
  | { kind: "error" };

/** Defense-in-depth timeout for the tracking lookup — a hung request (bad network, an edge that
 * swallows the API response) must resolve to a friendly error state, never an infinite spinner. */
const LOOKUP_TIMEOUT_MS = 15000;

export function TrackingPage(): JSX.Element {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { config } = useTheme();
  useDocumentTitle(pageTitle("Track your request", config.townName));
  const [state, setState] = useState<LoadState>({ kind: code ? "loading" : "idle" });
  const [lookup, setLookup] = useState("");
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<AttachmentView | null>(null);
  const liveRef = useRef<HTMLParagraphElement>(null);

  const load = useCallback((c: string) => {
    setState({ kind: "loading" });
    const timedOut = new Promise<never>((_, reject) => {
      window.setTimeout(() => reject(new Error("timeout")), LOOKUP_TIMEOUT_MS);
    });
    Promise.race([getPublicTracking(c), timedOut])
      .then((data) => {
        setState(data ? { kind: "found", data, fetchedAt: Date.now() } : { kind: "notfound" });
      })
      .catch((err) => {
        // A genuine 404 from the API resolves to `null` above (getPublicTrackingLive), not a
        // rejection — so any rejection here is an unexpected failure (network error, timeout, or
        // an edge/CDN returning something other than the API's JSON, e.g. an HTML shell instead of
        // a 404 body). Treat a 404 ApiError as not-found too, defense-in-depth against that class
        // of edge misconfiguration; everything else is a generic error state with a retry.
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: "notfound" });
        } else {
          setState({ kind: "error" });
        }
      });
  }, []);

  useEffect(() => {
    if (code) load(code);
  }, [code, load]);

  const onLookup = (e: React.FormEvent) => {
    e.preventDefault();
    const c = lookup.trim();
    if (!c) {
      setLookupError("Enter your tracking code.");
      return;
    }
    setLookupError(null);
    navigate(`/track/${encodeURIComponent(c)}`);
  };

  return (
    <ResidentThemeRoot className="td-track">
      <a href="#td-track-main" className="td-skip-link">
        Skip to status
      </a>
      <TownHeader embed={false} />
      <main id="td-track-main" className="td-track__main">
        {state.kind === "loading" ? <TrackingSkeleton /> : null}

        {state.kind === "error" && code ? (
          <section className="td-lookup" aria-labelledby="td-lookup-title">
            <h1 className="td-track__title" id="td-lookup-title">
              We couldn't load this
            </h1>
            <p className="td-alert td-alert--danger" role="alert">
              Something went wrong looking that up. Check your connection and try again.
            </p>
            <Button type="button" onClick={() => load(code)}>
              Try again
            </Button>
          </section>
        ) : null}

        {(state.kind === "idle" || state.kind === "notfound") && (
          <section className="td-lookup" aria-labelledby="td-lookup-title">
            <h1 className="td-track__title" id="td-lookup-title">
              Check a concern
            </h1>
            {state.kind === "notfound" ? (
              <p className="td-alert td-alert--danger" role="alert">
                We couldn't find a concern with that code. Check the code and try again.
              </p>
            ) : (
              <p className="td-track__sub">Enter the tracking code from your confirmation.</p>
            )}
            <form className="td-lookup__form" onSubmit={onLookup} noValidate>
              <Field id="track-code" label="Tracking code" error={lookupError ?? undefined}>
                {(aria) => (
                  <input
                    {...aria}
                    className="td-input td-input--code"
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="LV-XXXX-XXXX"
                    value={lookup}
                    onChange={(e) => setLookup(e.target.value)}
                  />
                )}
              </Field>
              <Button type="submit" fullWidth>
                Check status
              </Button>
            </form>
          </section>
        )}

        {state.kind === "found" ? (
          <FoundView
            data={state.data}
            fetchedAt={state.fetchedAt}
            onRefresh={() => load(state.data.trackingCode)}
            onOpenImage={setLightbox}
            liveRef={liveRef}
          />
        ) : null}
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
        <a href="#privacy" rel="noopener">
          Privacy
        </a>
      </footer>

      <Lightbox attachment={lightbox} onClose={() => setLightbox(null)} />
    </ResidentThemeRoot>
  );
}

function FoundView({
  data,
  fetchedAt,
  onRefresh,
  onOpenImage,
  liveRef,
}: {
  data: PublicTracking;
  fetchedAt: number;
  onRefresh: () => void;
  onOpenImage: (a: AttachmentView) => void;
  liveRef: React.RefObject<HTMLParagraphElement>;
}): JSX.Element {
  const { permitTypes } = useTheme();
  const nodes: TimelineNode[] = data.updates.map((u) => ({
    id: u.id,
    body: u.body,
    createdAt: u.createdAt,
    toStatus: u.toStatus,
    toKORAStatus: u.toKORAStatus,
    toPermitStatus: u.toPermitStatus,
  }));
  // A Question/KORA/permit request reuses this tracking view but gets kind-worded copy
  // (ask-a-question-faq.md §5.2 / records-request.md / roadmap module 6). KORA/permit each have
  // their OWN disjoint status value-space (never RequestStatus) — branch on `data.kind` before
  // reading a status label so a KORA/permit status never gets fed through the concern/question
  // status vocabulary.
  const isQuestion = data.kind === "question";
  const isKORA = data.kind === "kora";
  const isPermit = data.kind === "permit";
  const noun = isKORA ? "records request" : isQuestion ? "question" : isPermit ? "permit application" : "concern";
  const koraStatus = isKORA ? (data.status as KORAStatus) : null;
  const permitStatus = isPermit ? (data.status as PermitStatus) : null;
  const statusLine = koraStatus
    ? KORA_STATUS_LABELS[koraStatus]
    : permitStatus
      ? PERMIT_STATUS_LABELS[permitStatus]
      : STATUS_RESIDENT_LINE[data.status as RequestStatus];

  // Structured Inspections (permit-inspections.md Phase 1) — the resident checklist's EXPECTED slot
  // list comes from the permit type (matched by label, since the public tracking projection carries
  // only the resolved category LABEL, never the category id — see InspectionTypeDef doc). Undefined
  // when the type has no checklist (e.g. Water) or the label can't be matched (a renamed/deleted
  // type) — both render no checklist section, same "no checklist" posture as the staff panel.
  const permitInspectionTypes: InspectionTypeDef[] | undefined = isPermit
    ? permitTypes.find((t) => t.label === data.category)?.inspectionTypes
    : undefined;

  const [copied, setCopied] = useState(false);

  const copyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(data.trackingCode);
    } catch {
      // Clipboard blocked (e.g. some iframes) — select the text so Ctrl/Cmd-C still works.
      const el = document.getElementById("td-summary-code");
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [data.trackingCode]);

  return (
    <>
      {/* SR announcement of the current status on load. */}
      <p className="td-sr-only" aria-live="polite" ref={liveRef}>
        Current status:{" "}
        {koraStatus
          ? KORA_STATUS_LABELS[koraStatus]
          : permitStatus
            ? PERMIT_STATUS_LABELS[permitStatus]
            : statusText(data.status as RequestStatus)}
        . {statusLine}
      </p>

      <section className="td-summary" aria-labelledby="td-summary-title">
        <div className="td-summary__statusrow">
          <span className="td-sr-only" id="td-summary-title">
            Current status
          </span>
          {koraStatus ? (
            <KORAStatusPill status={koraStatus} />
          ) : permitStatus ? (
            <PermitStatusPill status={permitStatus} />
          ) : (
            <StatusPill status={data.status as RequestStatus} />
          )}
        </div>
        <p className="td-summary__line">{statusLine}</p>

        <dl className="td-summary__meta">
          <div>
            <dt>Submitted</dt>
            <dd>{shortDate(data.createdAt)}</dd>
          </div>
          <div>
            <dt>About</dt>
            <dd>{data.category}</dd>
          </div>
          <div>
            <dt>Location</dt>
            <dd>{data.address}</dd>
          </div>
        </dl>

        <div className="td-refcode">
          <span className="td-refcode__label" id="td-refcode-label">
            Your reference code
          </span>
          <div className="td-refcode__row">
            <code className="td-refcode__value td-mono" id="td-summary-code">
              {data.trackingCode}
            </code>
            <button
              type="button"
              className="td-refcode__copy"
              onClick={copyCode}
              aria-label={copied ? "Reference code copied" : "Copy reference code"}
            >
              {copied ? (
                <>
                  <CheckIcon width={16} height={16} aria-hidden="true" />
                  <span aria-hidden="true">Copied</span>
                </>
              ) : (
                <>
                  <CopyIcon width={16} height={16} aria-hidden="true" />
                  <span aria-hidden="true">Copy</span>
                </>
              )}
            </button>
          </div>
          <span className="td-refcode__hint">You'll need this to look up your {noun} again.</span>
        </div>

        <div className="td-summary__refresh">
          <span className="td-summary__updated">Updated {relativeTime(fetchedAt)}</span>
          <Button variant="ghost" onClick={onRefresh}>
            Refresh
          </Button>
        </div>
      </section>

      <section aria-labelledby="td-timeline-title">
        <h2 className="td-track__h2" id="td-timeline-title">
          Progress
        </h2>
        {nodes.length ? (
          <StatusTimeline nodes={nodes} />
        ) : (
          <p className="td-track__empty">Received. Waiting for a staff member to review.</p>
        )}
      </section>

      {permitInspectionTypes?.length ? (
        <InspectionsChecklist expected={permitInspectionTypes} updates={data.updates} />
      ) : null}

      {data.publicAttachments.length ? (
        <section aria-labelledby="td-pub-att-title">
          <h2 className="td-track__h2" id="td-pub-att-title">
            Photos &amp; documents
          </h2>
          <div className="td-tilegrid">
            {data.publicAttachments.map((a) => (
              <AttachmentTile key={a.id} attachment={a} onOpen={onOpenImage} />
            ))}
          </div>
        </section>
      ) : null}

      <section aria-labelledby="td-own-title" className="td-own">
        <h2 className="td-track__h2" id="td-own-title">
          {isKORA
            ? "Records sought"
            : isQuestion
              ? "Your question"
              : isPermit
                ? "Project description"
                : "What you reported"}
        </h2>
        <p className="td-own__desc">{data.ownSubmission.description}</p>
      </section>

      <ReplyBox trackingCode={data.trackingCode} isQuestion={isQuestion} onPosted={onRefresh} />

      {!isKORA && !isPermit && data.status === "Resolved" ? (
        <SatisfactionSurvey
          trackingCode={data.trackingCode}
          alreadySubmitted={Boolean(data.surveySubmitted)}
        />
      ) : null}

      <p className="td-track__notify">
        {data.hasContact
          ? "We'll also email or text you at each update."
          : `This ${noun} is anonymous, so check back here for updates.`}
      </p>

      <p className="td-track__asof">As of {absoluteDate(fetchedAt)}</p>
    </>
  );
}

/** A passed/failed inspection event's body text (handler.RecordInspection's PUBLIC audit-event
 *  format, e.g. "Inspection PASSED on 2026-08-05 — setback confirmed."). Matched case-sensitively
 *  on the fixed "Inspection PASSED/FAILED on " prefix the server always writes — never a fuzzy
 *  match on arbitrary staff note text. */
const INSPECTION_EVENT_RE = /^Inspection (PASSED|FAILED) on /;

/**
 * Resident read-only checklist (permit-inspections.md Phase 1 acceptance criteria: "a type with an
 * expected-inspection list renders a checklist on ... the resident tracking page"). IMPORTANT
 * HONESTY LIMIT: the PUBLIC tracking projection (shared.PublicRequestView) carries only free-text
 * audit-event bodies ("Inspection PASSED on 2026-08-05 — note") — it does NOT carry which checklist
 * slot (typeKey) each inspection was recorded against. This component therefore renders the
 * EXPECTED checklist labels from the permit type (an honest list of what's required) alongside an
 * UNDIFFERENTIATED "N of M inspections passed" count derived from counting pass/fail events in the
 * public timeline — it does NOT claim to know which specific slot(s) passed. If the public
 * projection is later extended to carry structured per-inspection rows (typeKey + status), this
 * should be upgraded to the same per-slot pill rendering as the staff panel (PermitDetailPage's
 * InspectionsPanel) instead of this count-only view.
 */
function InspectionsChecklist({
  expected,
  updates,
}: {
  expected: InspectionTypeDef[];
  updates: PublicTracking["updates"];
}): JSX.Element {
  const passedCount = updates.filter((u) => INSPECTION_EVENT_RE.exec(u.body)?.[1] === "PASSED").length;
  // Never over-report: more pass events than checklist slots (e.g. a re-inspection re-passing the
  // same slot) still reads as "all done", not "more than 100%".
  const passed = Math.min(passedCount, expected.length);

  return (
    <section aria-labelledby="td-inspections-title">
      <h2 className="td-track__h2" id="td-inspections-title">
        Inspections
      </h2>
      <p className="td-inspections__summary">
        {passed} of {expected.length} passed
      </p>
      <ul className="td-inspections__list">
        {expected.map((slot) => (
          <li key={slot.key} className="td-inspections__row">
            <span className="td-inspections__pill">
              <span aria-hidden="true">☐</span>
              <span>{slot.label}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="td-field__helper">Contact City Hall for the status of a specific inspection.</p>
    </section>
  );
}

/**
 * Resident reply / comment box (report-a-concern.md "Resident can reply / add a comment"; NFR-11).
 * Shown to anyone viewing the tracking page — holding the tracking code/link IS the auth model, no
 * login. A PUBLIC write, so it carries the same live-WAF CAPTCHA challenge as the intake form
 * (ensureCaptchaToken) before the POST; local dev/tests fall back to the checkbox gate like the
 * other public forms. On success the new comment is optimistically appended AND the tracking data
 * is re-fetched (onPosted) so it also reconciles with the server's timeline ordering.
 */
function ReplyBox({
  trackingCode,
  isQuestion,
  onPosted,
}: {
  trackingCode: string;
  isQuestion: boolean;
  onPosted: () => void;
}): JSX.Element {
  const noun = isQuestion ? "question" : "report";
  const ID = { comment: "td-reply-comment", captcha: "td-reply-captcha" };
  const [body, setBody] = useState("");
  const captcha = useCaptcha();
  const [state, setState] = useState<"idle" | "submitting" | "error" | "sent">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [posted, setPosted] = useState<Array<{ id: string; body: string; createdAt: string }>>([]);
  const resultRef = useRef<HTMLParagraphElement>(null);

  const trimmed = body.trim();
  const requiredMissing = !trimmed || !captcha.passed;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === "submitting") return;

    if (!trimmed) {
      setState("error");
      setErrorMsg("Enter a message before sending.");
      return;
    }
    if (!CAPTCHA_ENABLED && !captcha.passed) {
      setState("error");
      setErrorMsg("Please complete the spam check.");
      return;
    }

    // Submit safety net: reuse the fresh inline-solved token (fast no-op) or re-challenge if expired.
    if (!(await captcha.ensureForSubmit())) {
      const msg = "We couldn't verify the spam check. Please try the puzzle again.";
      setState("error");
      setErrorMsg(msg);
      return;
    }

    setState("submitting");
    setErrorMsg(null);
    try {
      const result = await addResidentComment(trackingCode, trimmed);
      setPosted((p) => [...p, result]);
      setBody("");
      setState("sent");
      onPosted();
    } catch (err) {
      setState("error");
      setErrorMsg(
        err instanceof ApiError ? err.message : "We couldn't send that — please try again.",
      );
    }
  };

  useEffect(() => {
    if (state === "sent") resultRef.current?.focus();
  }, [state]);

  return (
    <section aria-labelledby="td-reply-title" className="td-reply">
      <h2 className="td-track__h2" id="td-reply-title">
        Add to this {noun}
      </h2>
      <p className="td-track__sub">
        Have more information, or a question? Send a message — staff will see it on this {noun}.
      </p>

      {posted.length ? (
        <ul className="td-reply__posted">
          {posted.map((p) => (
            <li key={p.id} className="td-reply__postedItem">
              <p className="td-reply__postedBody">{p.body}</p>
              <time className="td-reply__postedTime" dateTime={p.createdAt}>
                {relativeTime(p.createdAt)}
              </time>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="td-sr-only" aria-live="polite" ref={resultRef} tabIndex={-1}>
        {state === "sent" ? "Message sent." : ""}
      </p>

      {state === "error" && errorMsg ? (
        <p className="td-alert td-alert--danger" role="alert">
          {errorMsg}
        </p>
      ) : null}

      <form
        className="td-form td-reply__form"
        noValidate
        onSubmit={onSubmit}
        aria-busy={state === "submitting"}
      >
        <Field id={ID.comment} label="Your message" required>
          {(aria) => (
            <>
              <textarea
                {...aria}
                className="td-input td-textarea"
                rows={3}
                maxLength={MAX_COMMENT_LEN}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={`Add a note or ask a question about this ${noun}…`}
              />
              {body.length > 400 ? (
                <span className="td-charcount" aria-live="polite">
                  {body.length} / {MAX_COMMENT_LEN}
                </span>
              ) : null}
            </>
          )}
        </Field>

        <CaptchaField
          ref={captcha.containerRef}
          id={ID.captcha}
          status={captcha.status}
          error={captcha.error ?? undefined}
          passed={captcha.passed}
          onChange={captcha.onChange}
          onRetry={captcha.retry}
        />

        <div className="td-submit">
          <Button type="submit" disabled={state === "submitting"} aria-disabled={requiredMissing}>
            {state === "submitting" ? (
              <>
                <SpinnerIcon width={18} height={18} />
                Sending…
              </>
            ) : (
              "Send message"
            )}
          </Button>
        </div>
      </form>
    </section>
  );
}

const SURVEY_RATINGS = [1, 2, 3, 4, 5] as const;
const SURVEY_RATING_LABELS: Record<number, string> = {
  1: "Very dissatisfied",
  2: "Dissatisfied",
  3: "Neutral",
  4: "Satisfied",
  5: "Very satisfied",
};

/**
 * Post-resolution satisfaction survey (report-a-concern.md "Satisfaction survey after
 * resolution"). Rendered by the caller ONLY when status === "Resolved". An accessible 1..5 radio
 * group (not raw <input type="range">/star buttons with no text alternative) + optional comment.
 * Same public-write CAPTCHA contract as ReplyBox. Shows a thank-you state after submit — and never
 * re-shows the form again this session once `alreadySubmitted` is true on load.
 */
function SatisfactionSurvey({
  trackingCode,
  alreadySubmitted,
}: {
  trackingCode: string;
  alreadySubmitted: boolean;
}): JSX.Element | null {
  const ID = { rating: "td-survey-rating", comment: "td-survey-comment", captcha: "td-survey-captcha" };
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const captcha = useCaptcha();
  const [state, setState] = useState<"idle" | "submitting" | "error" | "sent">(
    alreadySubmitted ? "sent" : "idle",
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [ratingError, setRatingError] = useState<string | null>(null);
  const thanksRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (state === "sent") thanksRef.current?.focus();
  }, [state]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === "submitting") return;

    if (!rating) {
      setRatingError("Choose a rating.");
      return;
    }
    setRatingError(null);
    if (!CAPTCHA_ENABLED && !captcha.passed) {
      setState("error");
      setErrorMsg("Please complete the spam check.");
      return;
    }

    // Submit safety net: reuse the fresh inline-solved token (fast no-op) or re-challenge if expired.
    if (!(await captcha.ensureForSubmit())) {
      const msg = "We couldn't verify the spam check. Please try the puzzle again.";
      setState("error");
      setErrorMsg(msg);
      return;
    }

    setState("submitting");
    setErrorMsg(null);
    try {
      await submitSatisfactionSurvey(trackingCode, rating, comment.trim() || undefined);
      setState("sent");
    } catch (err) {
      setState("error");
      setErrorMsg(
        err instanceof ApiError ? err.message : "We couldn't send that — please try again.",
      );
    }
  };

  if (state === "sent") {
    return (
      <section aria-labelledby="td-survey-title" className="td-survey td-survey--done">
        <h2 className="td-track__h2" id="td-survey-title" tabIndex={-1} ref={thanksRef}>
          Thanks for the feedback
        </h2>
        <p className="td-track__sub">Your rating helps us do better.</p>
      </section>
    );
  }

  return (
    <section aria-labelledby="td-survey-title" className="td-survey">
      <h2 className="td-track__h2" id="td-survey-title">
        How did we do?
      </h2>
      <p className="td-track__sub">This report is marked resolved — let us know how it went.</p>

      {state === "error" && errorMsg ? (
        <p className="td-alert td-alert--danger" role="alert">
          {errorMsg}
        </p>
      ) : null}

      <form
        className="td-form td-survey__form"
        noValidate
        onSubmit={onSubmit}
        aria-busy={state === "submitting"}
      >
        <div
          role="radiogroup"
          aria-labelledby={`${ID.rating}-label`}
          aria-describedby={ratingError ? `${ID.rating}-error` : undefined}
          className="td-survey__ratings"
        >
          <span className="td-field__label" id={`${ID.rating}-label`}>
            Rate your experience
            <span className="td-field__req" aria-hidden="true">
              {" "}
              *
            </span>
          </span>
          <div className="td-survey__stars">
            {SURVEY_RATINGS.map((n) => (
              <label key={n} className="td-survey__star">
                <input
                  type="radio"
                  name={ID.rating}
                  value={n}
                  checked={rating === n}
                  onChange={() => {
                    setRating(n);
                    setRatingError(null);
                  }}
                />
                <span aria-hidden="true">★</span>
                <span className="td-sr-only">
                  {n} star{n === 1 ? "" : "s"} — {SURVEY_RATING_LABELS[n]}
                </span>
              </label>
            ))}
          </div>
          {ratingError ? (
            <p className="td-field__error" id={`${ID.rating}-error`} role="alert">
              {ratingError}
            </p>
          ) : null}
        </div>

        <Field id={ID.comment} label="Anything you'd like to add?" optionalHint>
          {(aria) => (
            <textarea
              {...aria}
              className="td-input td-textarea"
              rows={3}
              maxLength={MAX_SURVEY_COMMENT_LEN}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
        </Field>

        <CaptchaField
          ref={captcha.containerRef}
          id={ID.captcha}
          status={captcha.status}
          error={captcha.error ?? undefined}
          passed={captcha.passed}
          onChange={captcha.onChange}
          onRetry={captcha.retry}
        />

        <div className="td-submit">
          <Button type="submit" disabled={state === "submitting"}>
            {state === "submitting" ? (
              <>
                <SpinnerIcon width={18} height={18} />
                Sending…
              </>
            ) : (
              "Submit rating"
            )}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** Skeleton for the tracking summary card + status row + a couple of update rows (states/spec §1). */
function TrackingSkeleton(): JSX.Element {
  return (
    <SkeletonRegion label="Looking up your concern…" className="td-skel-stack">
      <div className="td-skel-summary">
        <Skeleton variant="pill" />
        <Skeleton width="60%" height="var(--td-text-lg)" />
        <Skeleton width="40%" />
        <Skeleton width="50%" />
      </div>
      <div className="td-skel-updates">
        <div className="td-skel-update">
          <Skeleton variant="circle" width="12px" height="12px" />
          <Skeleton width="70%" />
        </div>
        <div className="td-skel-update">
          <Skeleton variant="circle" width="12px" height="12px" />
          <Skeleton width="55%" />
        </div>
        <div className="td-skel-update">
          <Skeleton variant="circle" width="12px" height="12px" />
          <Skeleton width="65%" />
        </div>
      </div>
    </SkeletonRegion>
  );
}

function statusText(s: RequestStatus): string {
  return s === "InProgress" ? "In Progress" : s === "Invalid" ? "Invalid or Rejected" : s;
}
